/**
 * Template API Service
 * 
 * Serviço para chamadas à API de templates (backend)
 * TODAS as chamadas passam pelo backend para proteger credenciais
 */

import { apiService } from './api';
import {
  OfficialApiInbox,
  WhatsAppTemplate,
  CreateTemplateData,
  TemplateListResponse,
  SyncTemplatesResponse,
  TemplateStats,
  TemplateValidationResponse
} from '../types/template';

class TemplateApiService {
  private readonly BASE_PATH = '/templates/official-api';

  /**
   * Busca caixas de entrada (números) do tenant atual
   * @returns Lista de caixas de entrada
   */
  async getInboxes(): Promise<OfficialApiInbox[]> {
    const response = await apiService.get(`${this.BASE_PATH}/inboxes`);
    return response.data || [];
  }

  /**
   * Lista templates de um número específico
   * @param phoneNumberId ID do número na Meta
   * @returns Lista de templates
   */
  async getTemplates(phoneNumberId: string): Promise<WhatsAppTemplate[]> {
    const response = await apiService.get(`${this.BASE_PATH}/${phoneNumberId}`);
    return response.data || [];
  }

  /**
   * Busca template específico pelo nome
   * @param phoneNumberId ID do número
   * @param templateName Nome do template
   * @returns Template encontrado
   */
  async getTemplateByName(
    phoneNumberId: string,
    templateName: string
  ): Promise<WhatsAppTemplate> {
    const response = await apiService.get(
      `${this.BASE_PATH}/${phoneNumberId}/${templateName}`
    );
    return response.data;
  }

  /**
   * Cria novo template
   * @param phoneNumberId ID do número
   * @param templateData Dados do template
   * @returns Resultado da criação
   */
  async createTemplate(
    phoneNumberId: string,
    templateData: CreateTemplateData
  ): Promise<any> {
    const response = await apiService.post(
      `${this.BASE_PATH}/${phoneNumberId}`,
      templateData
    );
    return response;
  }

  /**
   * Atualiza template existente
   * @param phoneNumberId ID do número
   * @param templateName Nome do template
   * @param templateData Novos dados
   * @returns Resultado da atualização
   */
  async updateTemplate(
    phoneNumberId: string,
    templateName: string,
    templateData: CreateTemplateData
  ): Promise<any> {
    const response = await apiService.put(
      `${this.BASE_PATH}/${phoneNumberId}/${templateName}`,
      templateData
    );
    return response;
  }

  /**
   * Deleta template
   * @param phoneNumberId ID do número
   * @param templateName Nome do template
   * @returns Resultado da exclusão
   */
  async deleteTemplate(
    phoneNumberId: string,
    templateName: string,
    templateId?: string
  ): Promise<any> {
    const params = new URLSearchParams();

    if (templateId) {
      params.set('hsm_id', templateId);
    }

    const queryString = params.toString();
    const response = await apiService.delete(
      `${this.BASE_PATH}/${encodeURIComponent(phoneNumberId)}/${encodeURIComponent(templateName)}${
        queryString ? `?${queryString}` : ''
      }`
    );
    return response;
  }

  /**
   * Sincroniza templates com Meta (atualiza status)
   * @param phoneNumberId ID do número
   * @returns Resultado da sincronização
   */
  async syncTemplates(phoneNumberId: string): Promise<SyncTemplatesResponse> {
    const response = await apiService.post(
      `${this.BASE_PATH}/${phoneNumberId}/sync`
    );
    return response;
  }

  /**
   * Obtém estatísticas dos templates
   * @param phoneNumberId ID do número
   * @returns Estatísticas
   */
  async getTemplateStats(phoneNumberId: string): Promise<TemplateStats> {
    const response = await apiService.get(
      `${this.BASE_PATH}/${phoneNumberId}/stats`
    );
    return response.data;
  }

  /**
   * Valida template antes de criar
   * @param templateData Dados do template
   * @returns Resultado da validação
   */
  async validateTemplate(
    templateData: CreateTemplateData
  ): Promise<TemplateValidationResponse> {
    const response = await apiService.post(
      `${this.BASE_PATH}/validate`,
      templateData
    );
    return response;
  }
}

export const templateApiService = new TemplateApiService();
